import React, { useState } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider } from '@mui/material';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import { Button, TextView } from '@atoms';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';

interface ExportReportsDialogProps {
  open: boolean;
  onClose: () => void;
  startDate?: Date;
  endDate?: Date;
  billingAccountId?: string;
  /** locationId of the selected billing account, when it has one. */
  locationId?: string;
  /** Name of the selected billing account, for the dialog subtitle. */
  billingAccountName?: string;
}

interface ExportOption {
  key: string;
  label: string;
  description: string;
  /** Builds the API path (relative to REACT_APP_API_BASE) for this export. */
  buildUrl: (query: URLSearchParams) => string;
  /** Whether the selected billing account narrows this export (by id). */
  useBillingAccount?: boolean;
  /** Whether the selected billing account narrows this export (by locationId). */
  useLocationId?: boolean;
  /** Whether the API expects the caller timezone offset header. */
  useTimezoneHeader?: boolean;
  buildFileName: (start?: DateTime, end?: DateTime) => string;
}

const rangeLabel = (start?: DateTime, end?: DateTime) =>
  `${start ? start.toFormat('LLL dd yyyy') : ''} - ${end ? end.toFormat('LLL dd yyyy') : ''}`;

const EXPORT_OPTIONS: ExportOption[] = [
  {
    key: 'sr-tokens',
    label: 'SR Tokens Export',
    description: 'AI processing token consumption for the selected range and account.',
    buildUrl: (query) => `/srt/csv-download?${query.toString()}`,
    useBillingAccount: true,
    useTimezoneHeader: true,
    buildFileName: () => `AI Processing ${DateTime.now().toFormat('LLL dd yyyy, hh:mm:ss')}.csv`
  },
  {
    key: 'qualified-leads',
    label: 'Qualified Leads Export',
    description: 'Qualified lead events across every company in the selected range.',
    // All locations: intentionally no locationId so every company is included.
    buildUrl: (query) => `/lead-event/qualified/export?${query.toString()}`,
    buildFileName: (start, end) => `Qualified Leads ${rangeLabel(start, end)}.csv`
  },
  {
    key: 'enquiries',
    label: 'Enquiries Export',
    description: 'Every enquiry received in the selected range, with contact, property and error detail.',
    // Sourced from the EnquriesMetaData table (zap entry/exit webhook records),
    // which is keyed by locationId rather than billingAccountId.
    useLocationId: true,
    buildUrl: (query) => `/enquiries/export?${query.toString()}`,
    buildFileName: (start, end) => `Enquiries ${rangeLabel(start, end)}.csv`
  }
];

const ExportReportsDialog: React.FC<ExportReportsDialogProps> = (props: ExportReportsDialogProps) => {
  const { open, onClose, startDate, endDate, billingAccountId, locationId, billingAccountName } = props;
  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  const start = startDate ? DateTime.fromJSDate(startDate) : undefined;
  const end = endDate ? DateTime.fromJSDate(endDate) : undefined;

  const download = async (option: ExportOption) => {
    setLoadingKey(option.key);
    const token = localStorage.getItem('token');
    const apiBase = process.env.REACT_APP_API_BASE;

    const query = new URLSearchParams();
    if (start) {
      query.append('startDate', start.toISODate() as string);
    }
    if (end) {
      query.append('endDate', end.toISODate() as string);
    }
    if (option.useBillingAccount && billingAccountId) {
      query.append('billingAccountId', billingAccountId);
    }
    if (option.useLocationId && locationId) {
      query.append('locationId', locationId);
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    if (option.useTimezoneHeader) {
      const zone: string[] = DateTime.now().toFormat('ZZ').split(':');
      const hour = Number(zone[0]);
      const mint = Number(zone[1]);
      headers.timezone = `${hour * 60 + mint}`;
    }

    try {
      const response: Response = await fetch(`${apiBase}${option.buildUrl(query)}`, {
        headers,
        method: 'GET'
      });

      if (response.status == 200) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', option.buildFileName(start, end));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        toast('Something went wrong');
      }
    } catch {
      toast('Something went wrong');
    } finally {
      setLoadingKey(null);
    }
  };

  const handleClose = () => {
    if (loadingKey) return;
    onClose();
  };

  /**
   * What the row will actually cover, so a filtered dashboard never implies a
   * filtered file. An account with no locationId can't narrow a locationId-keyed
   * export, and the qualified-leads endpoint is all-companies by design.
   */
  const scopeLabel = (option: ExportOption): string => {
    const scopable = option.useBillingAccount || option.useLocationId;
    if (!scopable) return 'All accounts (not filtered by the selected account)';
    if (!billingAccountName) return 'All accounts';
    if (option.useLocationId && !locationId) {
      return `All accounts — ${billingAccountName} has no location ID to filter on`;
    }
    return `Filtered to ${billingAccountName}`;
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <TextView $fontWeight={600} $fontSize={18}>
          Export Reports
        </TextView>
        <TextView $fontWeight={400} $fontSize={13} $fontColor="#6B6B6B">
          {`Reports cover ${rangeLabel(start, end)}`}
        </TextView>
      </DialogTitle>
      <DialogContent dividers>
        {EXPORT_OPTIONS.map((option, index) => (
          <Box key={option.key}>
            {index > 0 && <Divider />}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 2,
                gap: 2
              }}>
              <Box>
                <TextView $fontWeight={600} $fontSize={15}>
                  {option.label}
                </TextView>
                <TextView $fontWeight={400} $fontSize={13} $fontColor="#6B6B6B">
                  {option.description}
                </TextView>
                <TextView $fontWeight={500} $fontSize={12} $fontColor="#8A8A8A" mt={0.5}>
                  {scopeLabel(option)}
                </TextView>
              </Box>
              <Button
                label="Export"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => download(option)}
                loading={loadingKey === option.key}
                disabled={Boolean(loadingKey) && loadingKey !== option.key}
                sx={{ flexShrink: 0 }}
              />
            </Box>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button label="Close" variant="outlined" primary={false} onClick={handleClose} disabled={Boolean(loadingKey)} />
      </DialogActions>
    </Dialog>
  );
};

export default ExportReportsDialog;
